/* global runtime */
// Opprettelse av foretak, husholdning, medlemskap og invitasjoner.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const createBusiness = async ({
  country = 'NO',
  businessName = '',
  organizationNumber = '',
  address = '',
  clientType = 'business',
  companyTypeCode = '',
  companyTypeLabel = '',
  vatRegistered = null,
  // Svaret paa «var foretaket MVA-registrert allerede da regnskapet starter?».
  // Datoen regnes ut i backend av svaret; klienten sender svaret, ikke datoen.
  vatRegisteredAtAccountingStart = null,
  vatRegisteredFrom = '',
  onboardingPreferences = null,
  onboardingSetup = null,
  accountingStartupProfile = null,
  companyRestMode = null,
  deferActivation = false,
} = {}) => {
  const previousActiveBusinessId = runtime.activeBusinessId || null;
  const result = await authorizedPost('createBusinessHttp', {
    country,
    businessName,
    organizationNumber,
    address,
    clientType,
    companyTypeCode,
    companyTypeLabel,
    ...(typeof vatRegistered === 'boolean' ? { vatRegistered } : {}),
    ...(vatRegisteredAtAccountingStart === true ? { vatRegisteredAtAccountingStart: true } : {}),
    ...(vatRegisteredFrom ? { vatRegisteredFrom } : {}),
    ...(onboardingPreferences && typeof onboardingPreferences === 'object' ? { onboardingPreferences } : {}),
    ...(onboardingSetup && typeof onboardingSetup === 'object' ? { onboardingSetup } : {}),
    ...(accountingStartupProfile && typeof accountingStartupProfile === 'object' ? { accountingStartupProfile } : {}),
    ...(companyRestMode && typeof companyRestMode === 'object' ? { companyRestMode } : {}),
  });
  const business = result.business || null;
  await loadBusinessAccess();
  if (business?.businessId && deferActivation) {
    runtime.activeBusinessId = previousActiveBusinessId;
    if (runtime.user) {
      if (previousActiveBusinessId) localStorage.setItem(activeBusinessKey(runtime.user.uid), previousActiveBusinessId);
      else localStorage.removeItem(activeBusinessKey(runtime.user.uid));
    }
  }
  if (business?.businessId && !deferActivation) {
    runtime.activeBusinessId = business.businessId;
    if (runtime.user) localStorage.setItem(activeBusinessKey(runtime.user.uid), business.businessId);
    subscribeBusinessState();
  }
  emitRuntime();
  return business;
};
const checkBusinessAvailability = async ({ country = 'NO', organizationNumber = '' } = {}) => {
  const cleanCountry = String(country || 'NO').toUpperCase();
  const cleanOrganizationNumber = cleanCountry === 'NO'
    ? sanitizeOrganizationNumber(organizationNumber)
    : String(organizationNumber || '').replace(/\s+/g, '').toUpperCase();
  if (!cleanOrganizationNumber) return { available: true, exists: false, alreadyMember: false };
  return authorizedPost('checkBusinessAvailabilityHttp', {
    country: cleanCountry,
    organizationNumber: cleanOrganizationNumber,
  });
};

const createPersonalFinanceClient = async (preferences = {}) => createBusiness({
  country: 'NO',
  clientType: 'personal',
  businessName: 'Privatøkonomi',
  companyTypeCode: 'PERSONAL',
  companyTypeLabel: 'Privatøkonomi',
  vatRegistered: false,
  onboardingPreferences: {
    selectedUses: ['privat-personlig'],
    bankConnectionRequested: false,
    source: 'plumme-min-side',
    ...(preferences && typeof preferences === 'object' ? preferences : {}),
  },
});

const getPersonalFinanceHousehold = async ({ businessId } = {}) => authorizedPost('getPersonalFinanceHouseholdHttp', {
  businessId: businessId || undefined,
});

const createPersonalFinanceHouseholdInvite = async ({ businessId, email } = {}) => {
  const targetEmail = String(email || '').trim();
  if (!targetEmail) throw new Error(t('svc.emailRequired'));
  const payload = await authorizedPost('createPersonalFinanceHouseholdInviteHttp', { businessId, email: targetEmail });
  return payload.invite || null;
};

const respondPersonalFinanceHouseholdInvite = async ({ action, businessId, inviteId } = {}) => authorizedPost('respondPersonalFinanceHouseholdInviteHttp', {
  action,
  businessId: businessId || undefined,
  inviteId,
});

const cancelPersonalFinanceHouseholdInvite = async (inviteId) => authorizedPost('cancelPersonalFinanceHouseholdInviteHttp', { inviteId });

const hidePersonalFinanceHouseholdInvite = async (inviteId) => authorizedPost('hidePersonalFinanceHouseholdInviteHttp', { inviteId });

const resendPersonalFinanceHouseholdInvite = async (inviteId) => {
  if (!inviteId) throw new Error(t('svc.missingInvite'));
  const payload = await authorizedPost('resendPersonalFinanceHouseholdInviteHttp', { inviteId });
  return payload.invite || null;
};

const leavePersonalFinanceHousehold = async () => authorizedPost('leavePersonalFinanceHouseholdHttp');

const removeBusinessMember = async ({ businessId = runtime.activeBusinessId, memberUserId, reason = '' } = {}) => {
  if (!businessId) throw new Error(t('svc.pickBusiness'));
  if (!memberUserId) throw new Error(t('r1e.access.memberMissing'));
  const payload = await authorizedPost('removeBusinessMemberHttp', { businessId, memberUserId, reason });
  await loadBusinessAccess();
  emitRuntime();
  return payload || { businessId, memberUserId, status: 'removed' };
};

const leaveBusiness = async ({ businessId = runtime.activeBusinessId, reason = '' } = {}) => {
  if (!businessId) throw new Error(t('svc.pickBusiness'));
  const payload = await authorizedPost('leaveBusinessHttp', { businessId, reason });
  await loadBusinessAccess();
  subscribeBusinessState();
  emitRuntime();
  return payload || { businessId, status: 'removed' };
};

const createBusinessInvite = async ({ email, role = 'accountant', businessId = runtime.activeBusinessId } = {}) => {
  const targetEmail = String(email || '').trim();
  if (!targetEmail) throw new Error(t('svc.emailRequired'));
  if (!runtime.activeBusinessId) throw new Error(t('svc.pickBusiness'));
  const payload = await authorizedPost('createBusinessInviteHttp', {
    businessId,
    email: targetEmail,
    role,
  });
  await loadBusinessAccess();
  emitRuntime();
  return payload.invite || null;
};

const createBusinessOwnershipTransferInvite = async ({ email, previousOwnerRole = 'admin', reason = '', businessId = runtime.activeBusinessId } = {}) => {
  const targetEmail = String(email || '').trim();
  if (!targetEmail) throw new Error(t('svc.emailRequired'));
  if (!businessId) throw new Error(t('svc.pickBusiness'));
  const payload = await authorizedPost('createBusinessOwnershipTransferInviteHttp', {
    businessId,
    email: targetEmail,
    previousOwnerRole,
    reason,
  });
  await loadBusinessAccess();
  emitRuntime();
  return payload.invite || null;
};

const cancelBusinessInvite = async (inviteId) => {
  if (!inviteId) throw new Error(t('svc.missingInvite'));
  const payload = await authorizedPost('cancelBusinessInviteHttp', { inviteId });
  await loadBusinessAccess();
  emitRuntime();
  return payload || { inviteId, status: 'canceled' };
};

const resendBusinessInvite = async (inviteId) => {
  if (!inviteId) throw new Error(t('svc.missingInvite'));
  const payload = await authorizedPost('resendBusinessInviteHttp', { inviteId });
  await loadBusinessAccess();
  emitRuntime();
  return payload.invite || null;
};

const declineBusinessInvite = async (inviteId) => {
  if (!inviteId) throw new Error(t('svc.missingInvite'));
  const payload = await authorizedPost('declineBusinessInviteHttp', { inviteId });
  await loadBusinessAccess();
  emitRuntime();
  return payload || { inviteId, status: 'declined' };
};

const acceptBusinessInvite = async (inviteId) => {
  if (!inviteId) throw new Error(t('svc.missingInvite'));
  const payload = await authorizedPost('acceptBusinessInviteHttp', { inviteId });
  await loadBusinessAccess();
  const acceptedBusinessId = payload.membership?.businessId;
  if (acceptedBusinessId && runtime.memberships.some((membership) => membership.businessId === acceptedBusinessId)) {
    runtime.activeBusinessId = acceptedBusinessId;
    if (runtime.user) localStorage.setItem(activeBusinessKey(runtime.user.uid), acceptedBusinessId);
  }
  subscribeBusinessState();
  emitRuntime();
  return payload.membership || null;
};

const listBusinessAuditLogs = async (businessId = runtime.activeBusinessId, options = {}) => {
  await initFirebase();
  if (!runtime.db || !businessId) return [];
  if (!runtime.auth?.currentUser) throw new Error(t('svc.needLoginActivityLog'));
  const opts = typeof options === 'number' ? { maxCount: options } : (options || {});
  const maxCount = Number(opts.maxCount || opts.limit || 50);
  const year = Number(opts.year || 0);
  let query = runtime.db
    .collection('businesses')
    .doc(businessId)
    .collection('auditLogs')
    .orderBy('createdAt', 'desc');
  if (Number.isFinite(year) && year > 1900) {
    query = query.where('createdAt', '>=', `${year}-01-01T00:00:00.000Z`).where('createdAt', '<', `${year + 1}-01-01T00:00:00.000Z`);
  }
  const snapshot = await query.limit(Number.isFinite(maxCount) && maxCount > 0 ? maxCount : 50).get();
  return snapshot.docs.map((doc) => normalizeAuditLog(doc.id, doc.data() || {}));
};

// Tildeling paa et byraaoppdrag ER tilgangen til klientens regnskap: backend
// skriver et helt vanlig medlemsdokument paa foretaket naar noen settes paa
// oppdraget, og fjerner det naar oppdraget avsluttes (se
// functions/accountingFirms/accountingFirmMaterialization.ts).
//
// Derfor maa medlemskapene leses paa nytt her. Uten det ble den som nettopp
// satte seg selv paa oppdraget fortsatt nektet aa aapne regnskapet — helt til
// appen ble lastet paa nytt — og det saa ut som at byraaet manglet tilgang,
// mens det bare var fanen som ikke visste om den enna.
const firmAssignmentMutation = async (endpoint, payload) => {
  const result = await authorizedPost(endpoint, payload);
  await loadBusinessAccess();
  emitRuntime();
  return result;
};
